import { useDebounceFn } from 'ahooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@meitu/saas-ui';
import { BACKGROUND_IMG } from '../color-alpha';
import Saturation from '../color-saturation';
import { useLocaleStore } from '../i18n';
import {
    color as handleColor,
    hexToHsva,
    HsvaColor,
    hsvaToHex,
    hsvaToHexa,
    hsvaToRgba,
    RgbaColor,
    rgbaToHsva,
    validHex,
} from '../utils/color-convert';
import HexInput from './hex-input';
import './index.less';
import EditableInput from '../color-editable-input';
import EditableInputRGBA from '../color-editable-input-rgba';
import { SwatchPresetColor } from '../color-swatch';
import useRecentlyUsedColors from '../useRecentlyUsedColors';
import EyeDropper from './eye-dropper';
import ToolGroup from './tool-group';

const PRESET_COLORS = [
    '#FFFFFF',
    '#000000',
    '#FF2525',
    '#FF6200',
    '#FCCA00',
    '#27B148',
    '#00C7FF',
    '#4D9BF1',
    '#A411D1',
    '#8CA2AA',
    '#505559',
    '#FF7272',
    '#FF9959',
    '#FEDD59',
    '#52CC6F',
    '#59DBFF',
    '#7AB8FB',
    '#C465E2',
    '#C1CDD1',
    '#C2C7CC',
    '#FFB3B3',
    '#FFC9A6',
    '#FEEDA6',
    '#7EE697',
    '#A6ECFF',
    '#B8D9FD',
    '#E0ACEF',
    '#EDF1F2',
    '#E1E3E6',
    '#FFE9E9',
    '#FFEFE5',
    '#FFFAE5',
    '#DDFAE4',
    '#E5FAFF',
    '#EBF4FF',
    '#F6E7FB',
];

export interface ColorSketchPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'color'> {
    prefixCls?: string;
    width?: number;
    color?: string | RgbaColor;
    presetColors?: SwatchPresetColor[];
    editableDisable?: boolean;
    onChange?: (data: { color: string; transparent: boolean }) => void;
    onChangeComplete?: (data: { color: string; transparent: boolean }) => void;
    /** 是否使用RGBA输入框 */
    useRGBAInput?: boolean;
    transparent?: boolean;
    hideAlpha?: boolean;
    /** 隐藏无填充颜色 */
    hideEmptyColor?: boolean;
    beforeDraw?: () => {};
}

const ColorSketchPicker = React.forwardRef<HTMLDivElement, ColorSketchPickerProps>((props, ref) => {
    const {
        prefixCls = 'c-color-sketch-picker',
        className,
        onChange: _onChange,
        onChangeComplete,
        width = 280,
        presetColors = PRESET_COLORS,
        color,
        editableDisable = true,
        style,
        useRGBAInput = false,
        transparent,
        hideAlpha = false,
        hideEmptyColor = false,
        beforeDraw,
        ...other
    } = props;
    const { $t } = useLocaleStore();
    const lastChangeShadeRef = useRef<string>('');
    const [hsva, setHsva] = useState({ h: 209, s: 36, v: 90, a: 1 });
    const [hex, setHex] = useState('');
    const [alpha, setAlpha] = useState(0);
    const ua = navigator.userAgent.toLowerCase();
    const isPC = ua.indexOf('mtxx-pc') > -1;
    const isMac = ua.indexOf('mtxx-mac') > -1;
    const isClient = isPC || isMac;
    const hasEyeDropper = isClient ? false : beforeDraw || 'EyeDropper' in window ? true : false;
    const recentlyUsedColorsController = useRecentlyUsedColors('pure');
    // 初始化的时候就冻结起来, 期间新增颜色都不要同步到列表中
    const recentlyUsedColors = useMemo(() => recentlyUsedColorsController.colors, []);
    // 是否还没触发onChange, 这是为了解决直接输入框输入颜色, 输入框尚未失焦组件就销毁, 没触发onChange的问题
    const noTouchChangeRef = useRef<{ bool: boolean; hexValue: any; alphaValue: any }>({
        bool: false,
        hexValue: null,
        alphaValue: null,
    });
    // 是否还没触发onChangeComplete, 这是为了解决直接关闭ColorGroup, 组件销毁后, 只触发onChange而没触发onChangeComplete的问题
    const noTouchChangeCompleteRef = useRef<{ bool: boolean; arg: any }>({ bool: false, arg: null });
    const onChange: typeof _onChange = (data) => {
        _onChange?.(data);
        noTouchChangeRef.current = {
            bool: false,
            hexValue: null,
            alphaValue: null,
        };
        noTouchChangeCompleteRef.current = {
            bool: true,
            arg: data,
        };
    };
    const { run: changeAfter } = useDebounceFn(
        (data: { color: string; transparent: boolean }) => {
            lastChangeShadeRef.current = data.color;
            onChangeComplete?.(data);
            noTouchChangeCompleteRef.current = {
                bool: false,
                arg: data,
            };
        },
        {
            wait: 300,
        },
    );

    const isSaturationDragging = useRef(false);

    useEffect(() => {
        return () => {
            if (noTouchChangeRef.current.bool) {
                const { hexa, hsva } = handleColor(noTouchChangeRef.current.hexValue);
                const data = {
                    color: hexa,
                    transparent: hsva.a === 0,
                };
                onChange?.(data);
                onChangeComplete?.(data);
            } else if (noTouchChangeCompleteRef.current.bool) {
                onChangeComplete?.(noTouchChangeCompleteRef.current.arg);
            }
        };
    }, []);

    useEffect(() => {
        let newHsva: HsvaColor | null = null;
        if (typeof color === 'string' && validHex(color)) {
            newHsva = hexToHsva(color);
        }
        if (typeof color === 'object') {
            newHsva = rgbaToHsva(color);
        }
        if (color && newHsva) {
            newHsva.a = transparent ? 0 : newHsva?.a;
            const {
                hex,
                hsva: { a },
            } = handleColor(newHsva!);
            // hsv转rgb再转hsv, 则会丢失hue的精度，导致颜色不准确, 所以拖拽时需要存储Saturation组件正在使用的hue值, 防止色相丢失
            if (isSaturationDragging.current) {
                newHsva.h = hsva.h;
            }
            setHex(hex.slice(1, 7));
            setHsva(newHsva);
            setAlpha(Math.ceil(a * 100));
        }
    }, [color]);

    const handleChange = (hsv: HsvaColor) => {
        const {
            hex,
            hexa,
            hsva: { a },
        } = handleColor(hsv);
        setHex(hex.slice(1, 7));
        setAlpha(Math.ceil(a * 100));
        setHsva(hsv);
        onChange && onChange?.({ color: hexa, transparent: a === 0 });
        onChangeComplete && changeAfter({ color: hexa, transparent: a === 0 });
    };

    const getAlphaByInputValue = (value: string) => {
        let _alpha = +value;
        if (_alpha > 100) _alpha = 100;
        if (_alpha < 0) _alpha = 0;
        const alphaValue = _alpha === 0 ? 0 : _alpha || alpha;
        return alphaValue;
    };

    useEffect(() => {
        return () => {
            if (lastChangeShadeRef.current) {
                recentlyUsedColorsController.addColor(lastChangeShadeRef.current);
                lastChangeShadeRef.current = '';
            }
        };
    }, []);

    return (
        <div
            {...other}
            className={`${prefixCls} ${className || ''}`}
            ref={ref}
            style={{
                width,
                ...style,
            }}
        >
            <Saturation
                hsva={hsva}
                style={{ width: 'auto', height: 160, margin: '0 -16px' }}
                // 如果透明度为0，设置颜色, 需要变成1
                onChange={(newColor) => handleChange({ ...newColor, a: hsva.a === 0 ? 1 : hsva.a })}
                onDragStart={() => {
                    isSaturationDragging.current = true;
                }}
                onDragEnd={() => {
                    isSaturationDragging.current = false;
                }}
            />
            <ToolGroup
                useRGBAInput={useRGBAInput}
                hsva={hsva}
                hideAlpha={hideAlpha}
                hasEyeDropper={hasEyeDropper}
                handleChange={handleChange}
                setHex={setHex}
                setHsva={setHsva}
            />
            <div className="c-color-sketch-picker__menu">
                {hasEyeDropper && !useRGBAInput ? (
                    <EyeDropper setHex={setHex} setHsva={setHsva} handleChange={handleChange} />
                ) : null}
                {useRGBAInput ? (
                    <div className="c-color-sketch-picker__menu--inputRGBA">
                        <EditableInput
                            prefix="#"
                            label="Hex"
                            value={hex}
                            style={{ width: 68 }}
                            onChange={(e) => {
                                const result = handleColor(e.target.value);
                                if (result.hex) {
                                    setHex(result.hex.slice(1, 7));
                                    setHsva({ ...result.hsva, a: alpha / 100 });
                                    handleChange({ ...result.hsva, a: alpha / 100 });
                                } else {
                                    setHex(hsvaToHex(hsva).slice(1, 7));
                                }
                            }}
                        />
                        <EditableInputRGBA
                            hsva={hsva}
                            aProps={hideAlpha ? false : undefined}
                            onChange={(color) => {
                                handleChange(color.hsva);
                            }}
                        />
                    </div>
                ) : (
                    <>
                        <HexInput
                            hasEyeDropper={hasEyeDropper}
                            hideAlpha={hideAlpha}
                            hsva={hsva}
                            hex={hex}
                            setHex={setHex}
                            setHsva={setHsva}
                            handleChange={handleChange}
                            alpha={alpha}
                            uploadNoTouchChange={(data) => {
                                noTouchChangeRef.current = data;
                            }}
                        />
                        {!hideAlpha && (
                            <div className={`c-color-sketch-picker__menu--input ${hasEyeDropper ? '' : 'w120'}`}>
                                <Input
                                    suffix="%"
                                    value={alpha}
                                    onInput={(e) => {
                                        const alphaValue = getAlphaByInputValue(e.currentTarget.value);
                                        noTouchChangeRef.current = {
                                            bool: true,
                                            hexValue: hex,
                                            alphaValue,
                                        };
                                    }}
                                    onChange={(e) => setAlpha(getAlphaByInputValue(e.currentTarget.value))}
                                    onBlur={() => {
                                        handleChange({ ...hsva, a: alpha / 100 });
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="c-color-sketch-picker__expand">
                {recentlyUsedColors.length > 0 && (
                    <>
                        <div className="c-color-sketch-picker__expand--title">{$t((v) => v.Title_RecentlyUsed)}</div>
                        <div className="c-color-sketch-picker__expand--list" data-isrencentlyused="true">
                            {recentlyUsedColors.map((color) => (
                                <div key={color} style={{ background: `url(${BACKGROUND_IMG}) left center / 8px` }}>
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: color,
                                        }}
                                        onClick={() => {
                                            const result = handleColor(color);
                                            setHex(result.hex.slice(1, 7));
                                            setHsva(result.hsva);
                                            handleChange(result.hsva);
                                        }}
                                    ></div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <div className="c-color-sketch-picker__expand--title" style={{ marginTop: 4 }}>
                    {$t((v) => v.Title_PresetSolidColor)}
                </div>
                <div className="c-color-sketch-picker__expand--list">
                    {presetColors.map((color, index: number) => {
                        return (
                            <div
                                style={{ background: color as string }}
                                key={index}
                                onClick={() => {
                                    const result = handleColor(color as string);
                                    setHex(result.hex.slice(1, 7));
                                    setHsva(result.hsva);
                                    handleChange(result.hsva);
                                }}
                            ></div>
                        );
                    })}
                </div>
                {!hideEmptyColor && (
                    <>
                        <div className="c-color-sketch-picker__expand--title" style={{ marginTop: 4 }}>
                            {$t((v) => v.Title_NoFill)}
                        </div>
                        <div className="c-color-sketch-picker__expand--list">
                            <div
                                className="c-color-sketch-picker__expand--list-empty"
                                onClick={() => {
                                    const result = handleColor('#00000000');
                                    setHex(result.hex.slice(1, 7));
                                    setHsva(result.hsva);
                                    handleChange(result.hsva);
                                }}
                            ></div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
});

ColorSketchPicker.displayName = 'ColorSketchPicker';

export default React.memo(ColorSketchPicker);
